/**
 * The test track under the timeline editor (2026-10-13; plan `.notepads/test-track-timeline.md`).
 *
 * TRACK row (pinned under the ruler): under each timeline section, the waveform of the song stretch marked with
 * that section's name, bar for bar - the stretch its lights were made from. A song playing on OPBridge shows
 * where it is. SONG row (while marking): the whole song across the width with the marks as bands; pick a
 * section name, drag across the song to mark it. Settings (song, play on, tempo, play) are the detail panel's
 * `TestTrackProps` when the track row is selected. Rules: `timeline/testTrack.ts`; fetching: `testTrackStore.ts`.
 */
import { createEffect, createMemo, createSignal, For, on, onCleanup, onMount, Show, untrack, type Accessor, type JSX } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import {
  addClip, barSeconds, beatsPerBar, clipAt, clipSeconds, deleteClips, derivedClips, duplicateClipsAfter, effectiveClips, gridBpm,
  gridOf, markFor, moveClipsOver, clipOffBeat, inTimeDelta, pasteClipsAt, slipClip, songBars, splitClipsAt, trimClipsOver, waveformPath, type SongClip,
} from '../timeline/testTrack.ts';
import {
  beatmaps, importing, importTrack, lastError, loadTrack, looksLikeAudio, outputsOf, patchBeatmap, peaksById, play,
  playablePcs, refreshTracks, songMissing, status, stop, tracks, unavailableReason,
} from '../timeline/testTrackStore.ts';
import { toggleSongFlag, setTimelineSongClips, setTimelineSongSkipIntro, setTimelineTestTrack, setTimelineTestTrackPlace } from '../timeline/store.ts';
import { bridgeSkipsIntro, bridgeSongClips } from '../timeline/bridgeSync.ts';
import { ulid } from '../utils/ulid.ts';
import { importSongInto, linkSong } from './SongImport.tsx';
import { planFor, startFollowing } from '../timeline/songFollow.ts';
import { primaryBridge } from '../opbridge/store.ts';
import { pushToast } from '../ui/toast.ts';
import type { AudioKind, AudioOutput, TestTrackTempoChoice } from '../opbridge/httpClient.ts';
import { KIND_PLURAL, kindOf } from '../timeline/audioKind.ts';

interface SectionAt { id: string; name: string; start: number; bars: number }

/** The song row: a title strip over the waveform, as a clip in Live's Arrangement. */
const ROW_H = 64;
const CLIP_HD = 14;
const WAVE_H = ROW_H - CLIP_HD - 4;

/** Import a dropped or picked audio file and link the timeline to it once OPBridge has analysed it (the import
 *  window, `SongImport.tsx`, shows every stage). */
const importAndLink = importSongInto;
/** The first audio file in a drag, if any (types only during dragover; files on drop). */
const dragHasFiles = (e: DragEvent) => !!e.dataTransfer && [...e.dataTransfer.types].includes('Files');
function importLabel(): string | null {
  const i = importing();
  if (!i) return null;
  if (i.state === 'uploading') return `Uploading ${i.name}...`;
  if (i.state === 'analysing') return `Analysing ${i.name}: finding the beats, bar 1 and the drums...`;
  if (i.state === 'failed') return `${i.name}: ${i.message ?? 'the import failed'}`;
  return null;
}

/** Loads the timeline's track whenever it changes; shared by both rows and the panel. */
function useTrack(timeline: Accessor<Timeline>) {
  const id = () => timeline().testTrack?.id ?? null;
  createEffect(on(id, (tid) => { if (tid) void loadTrack(tid); }));
  // A song whose beat map is not there may be still analysing, or GONE from the library: the list says which
  // (2026-10-16, `songMissing`). Asked once per song that comes back without a map.
  createEffect(on(() => { const t = id(); return t ? beatmaps()[t] : undefined; }, (m) => { if (m === null) void refreshTracks(); }));
  return {
    id,
    map: () => { const t = id(); return t ? beatmaps()[t] : undefined; },
    peaks: () => { const t = id(); return t ? peaksById()[t] : undefined; },
  };
}

/** "9.3": timeline bar and beat (1-based) of beat `b`. */
const barBeatOf = (b: number, bpb: number) => `${Math.floor(b / bpb + 1e-6) + 1}.${Math.round(b - Math.floor(b / bpb + 1e-6) * bpb) + 1}`;
/** What a clip plays of the song: "bars 13-28", "bars 13.3-16.2" off the bar lines, "intro to bar 8" from before bar 1. */
function songBarsOf(c: SongClip, bpb: number): string {
  const start = c.fromBeat; const end = c.fromBeat + c.beats; // song beats, bar 1 = 0
  const whole = (x: number) => Math.abs(x / bpb - Math.round(x / bpb)) < 1e-6;
  const lastBar = (e: number) => (whole(e) ? String(Math.round(e / bpb)) : barBeatOf(e, bpb));
  if (start < -1e-6) return `intro to bar ${lastBar(end)}`;
  return whole(start) && whole(end) ? `bars ${Math.round(start / bpb) + 1}-${Math.round(end / bpb)}` : `bars ${barBeatOf(start, bpb)}-${barBeatOf(end - 1e-6, bpb)}`;
}

type TrackMenuEntry = { label: string; hint?: string; danger?: boolean; disabled?: boolean; run: () => void } | { sep: true };

/** The song clips selected in the editor (one editor at a time). The editor's own key handler acts on them (Delete,
 *  Ctrl/Cmd+E / D / C / V / A, arrows), because the editor keeps keyboard focus on itself after a pointer action. */
const [selectedClips, setSelectedClips] = createSignal<string[]>([]);
export { selectedClips };
/** Copied clips (Ctrl/Cmd+C), pasted at the playhead with Ctrl/Cmd+V. */
let clipboard: SongClip[] = [];

/** The clips a timeline plays as the song's grid is now (its own, or, before the first edit, what its section marks
 *  play), the beat map, and the grid to record with a write. Prunes a selection the list no longer has. */
function clipsOf(timeline: Timeline, sections: SectionAt[]) {
  const tt = timeline.testTrack;
  const m = tt?.id ? beatmaps()[tt.id] : undefined;
  if (!m || !tt) return null;
  const list = tt.clips ? effectiveClips(tt.clips, tt.clipsGrid, m) : derivedClips(m, sections);
  const sel = selectedClips();
  if (sel.some((id) => !list.some((c) => c.id === id))) setSelectedClips(sel.filter((id) => list.some((c) => c.id === id)));
  return { list, map: m, grid: gridOf(m) };
}
const write = (timeline: Timeline, cc: { grid: ReturnType<typeof gridOf> }, next: SongClip[] | null) => {
  if (next) setTimelineSongClips(timeline.id, next, cc.grid);
};

/** Delete the selected song clips; false when none is selected. */
export function deleteSelectedSongClips(timeline: Timeline, sections: SectionAt[]): boolean {
  const c = clipsOf(timeline, sections); const ids = selectedClips();
  if (!ids.length || !c) return false;
  write(timeline, c, deleteClips(c.list, ids));
  setSelectedClips([]);
  return true;
}

/** Ctrl/Cmd+E: split the selected clips (or the one under it) at timeline bar `bar` (the playhead). */
export function splitSongClipsAt(timeline: Timeline, sections: SectionAt[], bar: number): boolean {
  const c = clipsOf(timeline, sections);
  if (!c) return false;
  const next = splitClipsAt(c.list, selectedClips(), bar * beatsPerBar(c.map), ulid);
  if (!next) return false;
  write(timeline, c, next);
  return true;
}

/** Ctrl/Cmd+D: duplicate the selected clips straight after themselves; the copies become the selection. */
export function duplicateSelectedSongClips(timeline: Timeline, sections: SectionAt[]): boolean {
  const c = clipsOf(timeline, sections);
  if (!c) return false;
  const r = duplicateClipsAfter(c.list, selectedClips(), ulid);
  if (!r) return false;
  write(timeline, c, r.clips);
  setSelectedClips(r.ids);
  return true;
}

/** Ctrl/Cmd+A on the song row: every clip. */
export function selectAllSongClips(timeline: Timeline, sections: SectionAt[]): boolean {
  const c = clipsOf(timeline, sections);
  if (!c || !c.list.length) return false;
  setSelectedClips(c.list.map((x) => x.id));
  return true;
}

/** Ctrl/Cmd+C: copy the selected clips. */
export function copySelectedSongClips(timeline: Timeline, sections: SectionAt[]): boolean {
  const c = clipsOf(timeline, sections); const ids = selectedClips();
  if (!c || !ids.length) return false;
  clipboard = c.list.filter((x) => ids.includes(x.id)).map((x) => ({ ...x }));
  return clipboard.length > 0;
}

/** Ctrl/Cmd+V: paste the copied clips at timeline bar `bar` (the playhead); they become the selection. */
export function pasteSongClipsAt(timeline: Timeline, sections: SectionAt[], bar: number): boolean {
  const c = clipsOf(timeline, sections);
  if (!c || !clipboard.length) return false;
  const r = pasteClipsAt(c.list, clipboard, Math.round(bar * beatsPerBar(c.map)), ulid);
  if (!r) return false;
  write(timeline, c, r.clips);
  setSelectedClips(r.ids);
  return true;
}

/** Arrow keys on the song row: move the selected clips by `deltaBeats` (a bar, or a beat with Shift). */
export function nudgeSelectedSongClips(timeline: Timeline, sections: SectionAt[], deltaBeats: number): boolean {
  const c = clipsOf(timeline, sections); const ids = selectedClips();
  if (!c || !ids.length) return false;
  write(timeline, c, moveClipsOver(c.list, ids, deltaBeats, ulid));
  return true;
}

/** The song row's right-click (and long-press) menu at timeline bar `bar`: split there (bar line or beat) or at the
 *  playhead, duplicate, copy, paste, delete, add a clip in a gap, or go back to the section marks. The first edit
 *  writes out what the marks play. Null when there is no analysed song. */
/** A shortcut as this computer writes it: Cmd on a Mac, Ctrl elsewhere (2026-10-16: the song menu said Ctrl on a Mac). */
const keyOf = (k: string): string => (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? `\u2318${k}` : `Ctrl+${k}`);

export function songTrackMenu(timeline: Timeline, sections: SectionAt[], bar: number, playheadBar?: number): { title: string; entries: TrackMenuEntry[] } | null {
  const cc = clipsOf(timeline, sections);
  if (!cc) return null;
  const { list: base, map: m } = cc;
  const own = timeline.testTrack?.clips ?? null;
  const bpb = beatsPerBar(m);
  const beat = bar * bpb;
  const onBar = Math.round(beat / bpb) * bpb;
  const onBeat = Math.round(beat);
  const set = (next: SongClip[] | null) => write(timeline, cc, next);
  const c = clipAt(base, beat);
  if (c && !selectedClips().includes(c.id)) setSelectedClips([c.id]);
  const sel = selectedClips().filter((id) => base.some((x) => x.id === id));
  const entries: TrackMenuEntry[] = [];
  if (c) {
    const atBar = splitClipsAt(base, [c.id], onBar, ulid);
    entries.push({ label: `Split at ${barBeatOf(onBar, bpb)}`, disabled: !atBar, run: () => set(atBar) });
    if (onBeat !== onBar) {
      const atBeat = splitClipsAt(base, [c.id], onBeat, ulid);
      entries.push({ label: `Split at ${barBeatOf(onBeat, bpb)} (the beat)`, disabled: !atBeat, run: () => set(atBeat) });
    }
    if (playheadBar !== undefined) {
      const atPh = splitClipsAt(base, sel, playheadBar * bpb, ulid);
      entries.push({ label: 'Split at the playhead', hint: keyOf('E'), disabled: !atPh, run: () => set(atPh) });
    }
    const off = clipOffBeat(c, bpb);
    if (off !== 0) {
      entries.push({ label: 'Put back in time', hint: `${Math.abs(off).toFixed(2)} beat off`, run: () => set(moveClipsOver(base, sel.length ? sel : [c.id], inTimeDelta(base, sel.length ? sel : [c.id], c, 0, bpb), ulid)) });
    }
    const dup = duplicateClipsAfter(base, sel, ulid);
    entries.push({ label: sel.length > 1 ? `Duplicate ${sel.length} clips` : 'Duplicate', hint: keyOf('D'), disabled: !dup, run: () => { if (dup) { set(dup.clips); setSelectedClips(dup.ids); } } });
    entries.push({ label: sel.length > 1 ? `Copy ${sel.length} clips` : 'Copy', hint: keyOf('C'), run: () => { copySelectedSongClips(timeline, sections); } });
    entries.push({ label: sel.length > 1 ? `Delete ${sel.length} clips` : 'Delete', hint: 'Del', danger: true, run: () => { set(deleteClips(base, sel)); setSelectedClips([]); } });
  } else {
    // A gap: start at the bar line, or where the clip before it ends when that is later (a gap that starts mid-bar).
    const prevEnd = Math.max(0, ...base.filter((x) => x.atBeat + x.beats <= beat + 1e-6).map((x) => x.atBeat + x.beats));
    const added = addClip(m, base, Math.max(Math.floor(beat / bpb) * bpb, prevEnd), 4 * bpb, ulid);
    entries.push({ label: 'Add a clip here', hint: 'the song carrying on', disabled: !added, run: () => set(added) });
  }
  if (clipboard.length) {
    const at = playheadBar !== undefined ? playheadBar : onBar / bpb;
    entries.push({ label: `Paste ${clipboard.length > 1 ? `${clipboard.length} clips ` : ''}at the playhead`, hint: keyOf('V'), run: () => { pasteSongClipsAt(timeline, sections, at); } });
  }
  if (own) {
    entries.push({ sep: true });
    entries.push({ label: 'Back to the section marks', hint: 'drops these clips', danger: true, run: () => { setTimelineSongClips(timeline.id, null); setSelectedClips([]); } });
  }
  return { title: `Song at ${barBeatOf(onBeat, bpb)}`, entries };
}

export function TestTrackRow(props: {
  timeline: Timeline;
  sections: SectionAt[];
  ppb: number;
  gridStyle: JSX.CSSProperties;
  selected: boolean;
  onSelect: () => void;
  /** The editor's playhead (timeline bars) while the TIMELINE plays, else null. */
  timelineBar?: number | null;
  /** A click on empty song: move the editor's playhead there (nearest bar). */
  onSeek?: (bar: number) => void;
  /** True once to open the song picker (the editor's "Song" pill adds the row with the picker open); the row calls
   *  `onPickerOpened` straight away so it fires once. A counter here fired on every timeline edit: Solid's `on()`
   *  re-runs with the same value when the props spread re-evaluates (found by timeline_test_track 4b / 5f). */
  openPickerNow?: boolean;
  onPickerOpened?: () => void;
  /** "Remove the song row" in the picker, while the timeline has no song. */
  onRemoveRow?: () => void;
}): JSX.Element {
  const tr = useTrack(() => props.timeline);
  // The song picker (operator, 2026-10-14: "i should be able to click this text to see a song picker, right now
  // there's no way of adding a song direct from audio files section"): the audio library by type, songs first, an
  // import, and taking the row away again. Fixed to the screen under the row's text so no scroll box cuts it off.
  const [pickAt, setPickAt] = createSignal<{ left: number; top?: number; bottom?: number; maxH: number } | null>(null);
  let pickBtn: HTMLButtonElement | undefined;
  let pickFile: HTMLInputElement | undefined;
  let rowEl: HTMLDivElement | undefined;
  const openPicker = () => {
    void refreshTracks();
    // Under the row's text when there is no song yet; under the row's head when choosing another one.
    const r = (pickBtn?.isConnected ? pickBtn : rowEl?.querySelector('.tl2-h') as HTMLElement | null)?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    setPickAt(below >= 220 || below >= above
      ? { left: r.left, top: r.bottom + 4, maxH: Math.max(160, below) }
      : { left: r.left, bottom: window.innerHeight - r.top + 4, maxH: Math.max(160, above) });
  };
  createEffect(() => { if (props.openPickerNow) { untrack(() => props.onPickerOpened?.()); queueMicrotask(openPicker); } });
  const pickGroups = () => {
    const all = tracks().filter((t) => t.analysed || t.analysing || t.arriving);
    const of = (k: AudioKind) => all.filter((t) => kindOf(t, beatmaps()[t.id]) === k).sort((a, b) => a.name.localeCompare(b.name));
    return (['song', 'loop', 'oneshot'] as AudioKind[]).map((k) => ({ k, list: of(k) })).filter((g) => g.list.length > 0);
  };
  const choose = (t: { id: string; name: string }) => { void linkSong(props.timeline.id, { id: t.id, name: t.name }); setPickAt(null); };
  {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && pickAt()) { e.preventDefault(); e.stopPropagation(); setPickAt(null); } };
    const onDown = (e: PointerEvent) => { if (pickAt() && !(e.target as HTMLElement | null)?.closest('.tl2-songpick, .tl2-trk-pick')) setPickAt(null); };
    onMount(() => { window.addEventListener('keydown', onKey, { capture: true }); window.addEventListener('pointerdown', onDown, { capture: true }); });
    onCleanup(() => { window.removeEventListener('keydown', onKey, { capture: true }); window.removeEventListener('pointerdown', onDown, { capture: true }); });
  }
  const playingHere = () => { const s = status(); return !!s?.playing && s.id === tr.id(); };
  const [dropOver, setDropOver] = createSignal(false);
  // A failed import shows on the row for a few seconds, then leaves the clips visible; the panel keeps the message.
  const [failShown, setFailShown] = createSignal(false);
  createEffect(on(importing, (i) => {
    if (i?.state !== 'failed') return;
    setFailShown(true);
    const t = setTimeout(() => setFailShown(false), 8000);
    onCleanup(() => clearTimeout(t));
  }));
  const rowNotice = () => (importing()?.state === 'failed' && !failShown() ? null : importLabel());
  // A selection belongs to one timeline: a different timeline (or song) starts with none.
  // A memo, not a bare accessor: `on()` does not dedupe, and the timeline object is replaced on every edit, so the
  // bare form cleared the selection after each drag (found 2026-10-13 by logging every selection change).
  const selKey = createMemo(() => `${props.timeline.id}/${props.timeline.testTrack?.id ?? ''}`);
  createEffect(on(selKey, () => setSelectedClips([]), { defer: true }));
  // ---- clips, Ableton-style (2026-10-13): click selects (Shift / Ctrl adds, on release), drag moves every selected
  // clip, an edge trims every selected clip, Alt-drag slips the audio inside them; whatever a clip lands on is
  // overwritten. Bars; Shift = beats; Ctrl/Cmd = no snap. Esc cancels a drag. A drag on empty song selects a box.
  const ownClips = () => { const tt = props.timeline.testTrack; const m = tr.map(); return tt?.clips && m ? effectiveClips(tt.clips, tt.clipsGrid, m) : null; };
  const derived = createMemo(() => { const m = tr.map(); return m && !props.timeline.testTrack?.clips ? derivedClips(m, props.sections) : null; });
  const clips = () => ownClips() ?? derived();
  const [clipDrag, setClipDrag] = createSignal<{ clips: SongClip[]; mode: 'move' | 'trim' | 'slip' } | null>(null);
  const [band, setBand] = createSignal<{ x0: number; x1: number } | null>(null);
  const shownClips = () => clipDrag()?.clips ?? clips();
  const isSel = (id: string) => selectedClips().includes(id);
  const bpb = () => { const m = tr.map(); return m ? beatsPerBar(m) : 4; };
  function startClipDrag(e: PointerEvent) {
    if (e.button !== 0) return;
    const m = tr.map(); const list = clips();
    const cell = e.currentTarget as HTMLElement;
    const left = () => cell.getBoundingClientRect().left;
    const el = (e.target as Element).closest('[data-clip-id]') as HTMLElement | null;
    if (!el) {
      // Empty song: a drag selects the clips it spans (a box), a click clears the selection and moves the playhead.
      if (e.pointerType === 'touch' || !list) return;
      const x0 = e.clientX - left(); const add = e.shiftKey || e.ctrlKey || e.metaKey; const before = selectedClips();
      cell.setPointerCapture(e.pointerId);
      let moved = false;
      const mv = (ev: PointerEvent) => {
        const x1 = ev.clientX - left();
        if (!moved && Math.abs(x1 - x0) < 3) return;
        moved = true;
        setBand({ x0: Math.min(x0, x1), x1: Math.max(x0, x1) });
        const a = (Math.min(x0, x1) / props.ppb) * bpb(); const b = (Math.max(x0, x1) / props.ppb) * bpb();
        const hit = list.filter((c) => c.atBeat < b && c.atBeat + c.beats > a).map((c) => c.id);
        setSelectedClips(add ? [...new Set([...before, ...hit])] : hit);
      };
      const upB = () => {
        cell.removeEventListener('pointermove', mv); cell.removeEventListener('pointerup', upB); cell.removeEventListener('pointercancel', upB);
        setBand(null);
        if (!moved) { if (!add) setSelectedClips([]); props.onSeek?.(x0 / props.ppb); }
        props.onSelect();
      };
      cell.addEventListener('pointermove', mv); cell.addEventListener('pointerup', upB); cell.addEventListener('pointercancel', upB);
      return;
    }
    if (!m || !list) return;
    const id = el.dataset.clipId!;
    const edge = (e.target as HTMLElement).dataset.edge as 'start' | 'end' | undefined;
    const c0 = list.find((c) => c.id === id);
    if (!c0) return;
    const toggle = e.shiftKey || e.ctrlKey || e.metaKey;
    // A plain press on an unselected clip selects it alone at once, so a drag carries it. Shift / Ctrl change the
    // selection only on a CLICK (released without moving): during a drag they mean beats / no snap (the review,
    // 2026-10-13: Shift at the press used to pull the clip in or out of the selection).
    if (!isSel(id) && !toggle) setSelectedClips([id]);
    props.onSelect();
    const per = beatsPerBar(m);
    const beatAtX = (x: number) => ((x - left()) / props.ppb) * per;
    const snap = (b: number, ev: PointerEvent) => (ev.ctrlKey || ev.metaKey ? b : Math.round(b / (ev.shiftKey ? 1 : per)) * (ev.shiftKey ? 1 : per));
    const x0 = e.clientX; const b0 = beatAtX(x0);
    const mode: 'move' | 'trim' | 'slip' = edge ? 'trim' : e.altKey ? 'slip' : 'move';
    const ids = isSel(id) ? selectedClips() : [id];
    const before = JSON.stringify(list);
    let moved = false; let cancelled = false;
    e.preventDefault();
    cell.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      if (cancelled || (!moved && Math.abs(ev.clientX - x0) < 3)) return;
      moved = true;
      const next = mode === 'trim'
        ? trimClipsOver(m, list, ids, edge!, snap(beatAtX(ev.clientX), ev) - (edge === 'start' ? c0.atBeat : c0.atBeat + c0.beats), 1, ulid)
        : mode === 'slip'
          ? slipClip(m, list, ids, -snap(beatAtX(ev.clientX) - b0, ev))
          // The MUSIC snaps (2026-10-16): the song's bar lines land on the timeline's, so a clip that starts in the intro
          // keeps time, and the start of the timeline stops it at the first in-time place. Ctrl/Cmd: free.
          : moveClipsOver(list, ids, ev.ctrlKey || ev.metaKey ? beatAtX(ev.clientX) - b0 : inTimeDelta(list, ids, c0, beatAtX(ev.clientX) - b0, ev.shiftKey ? 1 : per), ulid);
      setClipDrag({ clips: next, mode });
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); cancelled = true; setClipDrag(null); } };
    window.addEventListener('keydown', onKey, { capture: true });
    const up = (ue?: Event) => {
      cell.removeEventListener('pointermove', move);
      cell.removeEventListener('pointerup', up);
      cell.removeEventListener('pointercancel', up);
      window.removeEventListener('keydown', onKey, { capture: true });
      const d = clipDrag();
      setClipDrag(null);
      // A pointercancel (the system took the touch, a second finger) is a cancel, like Esc: it used to commit (D-4).
      if (cancelled || ue?.type === 'pointercancel') return;
      if (!moved) { if (toggle) setSelectedClips(isSel(id) ? selectedClips().filter((x) => x !== id) : [...selectedClips(), id]); return; }
      if (d && JSON.stringify(d.clips) !== before) {
        setTimelineSongClips(props.timeline.id, d.clips, gridOf(m));
        // A free drag (or a slip off the grid) that leaves the music out of time with the bars: say so, and how back.
        const off = d.clips.filter((c) => ids.includes(c.id) && clipOffBeat(c, per) !== 0 && clipOffBeat(list.find((x) => x.id === c.id) ?? c, per) === 0);
        if (off.length) pushToast('warn', `The song is now out of time with the bars (${Math.abs(clipOffBeat(off[0]!, per)).toFixed(2)} beat off). Right-click the clip for "Put back in time", or ${keyOf('Z')} to undo.`, 7000);
      }
    };
    cell.addEventListener('pointermove', move);
    cell.addEventListener('pointerup', up);
    cell.addEventListener('pointercancel', up);
  }
  /** Where the song is, in timeline bars: the editor's own playhead while the timeline plays (the song follows it);
   *  while the song plays alone from its panel, the clip playing that music nearest the last place. */
  const playX = createMemo(() => {
    if (props.timelineBar != null) return props.timelineBar * props.ppb;
    const s = status(); const m = tr.map();
    if (!playingHere() || !m || s?.bar == null) return null;
    const songBar = s.bar + ((s.beat ?? 1) - 1) / Math.max(1, Number(String(m.sig).split('/')[0]) || 4);
    const per = beatsPerBar(m); const sb = (songBar - 1) * per;
    const c = (clips() ?? []).find((x) => sb >= x.fromBeat && sb < x.fromBeat + x.beats);
    return c ? ((c.atBeat + sb - c.fromBeat) / per) * props.ppb : null;
  });
  return (
    <div
      class="tl2-row tl2-track"
      ref={rowEl}
      style={{ height: `${ROW_H}px` }}
      aria-selected={props.selected}
      data-testid="tl2-track"
      data-drop={dropOver() ? 'true' : undefined}
      onDragOver={(e) => { if (!dragHasFiles(e)) return; e.preventDefault(); e.dataTransfer!.dropEffect = 'copy'; setDropOver(true); }}
      onDragLeave={() => setDropOver(false)}
      onDrop={(e) => {
        setDropOver(false);
        const f = [...(e.dataTransfer?.files ?? [])].find(looksLikeAudio) ?? e.dataTransfer?.files?.[0];
        if (!f) return;
        e.preventDefault();
        void importAndLink(props.timeline, f);
      }}
    >
      <div class="tl2-h" onClick={() => props.onSelect()} data-info="The song this timeline is built against. Click for its settings: which song, where it plays, play and stop.">
        <span class="tl2-trk-i" aria-hidden="true">♪</span>
        <span class="tl2-trk-nm">{props.timeline.testTrack?.name ?? 'Song'}</span>
        <Show when={playingHere()}><em class="tl2-trk-on">playing</em></Show>
        {/* Muted or soloed, said where a lane says it: click to undo (2026-10-16). */}
        <span class="tl2-ms">
          <Show when={props.timeline.testTrack?.mute}>
            <button type="button" data-k="m" title="Muted: click to unmute" aria-label="Unmute the song" onClick={(e) => { e.stopPropagation(); toggleSongFlag(props.timeline.id, 'mute'); }}>M</button>
          </Show>
          <Show when={props.timeline.testTrack?.solo}>
            <button type="button" data-k="s" title="Soloed: click to unsolo" aria-label="Unsolo the song" onClick={(e) => { e.stopPropagation(); toggleSongFlag(props.timeline.id, 'solo'); }}>S</button>
          </Show>
        </span>
      </div>
      <div class="tl2-c tl2-grid" style={props.gridStyle} data-sliding={clipDrag() ? clipDrag()!.mode : undefined} data-clips="true"
        onPointerDown={startClipDrag}
        data-info="Click a clip to select it (Shift adds); drag on empty song to select a box. Drag to move, an edge to trim, Alt-drag to slip the audio. Bars; Shift = beats; Ctrl = no snap; Esc cancels. Ctrl+E split at the playhead, Ctrl+D duplicate, Ctrl+C / V copy and paste, arrows nudge, Del delete.">
        <Show
          when={props.timeline.testTrack}
          fallback={
            <>
              <button type="button" class="tl2-trk-hint tl2-trk-pick" ref={pickBtn} data-testid="tl2-song-pick" onClick={() => (pickAt() ? setPickAt(null) : openPicker())}>
                {importLabel() ?? unavailableReason() ?? 'Choose a song, or drop an audio file here, to see it under this timeline'}
              </button>
              <input ref={pickFile} type="file" accept="audio/*,.mp3,.wav,.flac,.ogg,.aif,.aiff,.m4a" hidden
                onChange={(e) => { const f = e.currentTarget.files?.[0]; e.currentTarget.value = ''; setPickAt(null); if (f) void importAndLink(props.timeline, f); }} />
            </>
          }
        >
          <Show when={tr.map() !== undefined} fallback={<span class="tl2-trk-hint">Loading the song...</span>}>
            <Show when={tr.map()} fallback={
              <Show
                when={songMissing(props.timeline.testTrack?.id)}
                fallback={<span class="tl2-trk-hint">{unavailableReason() ?? 'This song is not analysed yet, so it has no beats to line up.'}</span>}
              >
                {/* Said plainly, with the way out (2026-10-16). */}
                <button type="button" class="tl2-trk-hint tl2-trk-pick" data-testid="tl2-song-missing" onClick={() => props.onSelect()}>
                  "{props.timeline.testTrack?.name ?? 'This song'}" is no longer in the audio library. Click to choose another song or remove it.
                </button>
              </Show>
            }>
              <Show when={shownClips()}>
                <For each={shownClips()!}>
                  {(c) => {
                    const left = () => (c.atBeat / bpb()) * props.ppb;
                    const w = () => (c.beats / bpb()) * props.ppb;
                    const secs = () => clipSeconds(tr.map()!, c);
                    return (
                      <div
                        class="tl2-clip"
                        data-clip-id={c.id}
                        data-at={c.atBeat}
                        data-from={c.fromBeat}
                        data-beats={c.beats}
                        data-derived={props.timeline.testTrack?.clips ? undefined : 'true'}
                        data-offbeat={clipOffBeat(c, bpb()) !== 0 ? 'true' : undefined}
                        title={clipOffBeat(c, bpb()) !== 0 ? `Out of time with the bars by ${Math.abs(clipOffBeat(c, bpb())).toFixed(2)} beat: right-click, Put back in time` : undefined}
                        aria-selected={isSel(c.id)}
                        aria-label={`Song clip at ${barBeatOf(c.atBeat, bpb())}: song ${songBarsOf(c, bpb())}`}
                        style={{ left: `${left()}px`, width: `${w()}px` }}
                      >
                        <div class="tl2-clip-hd"><span>{songBarsOf(c, bpb())}</span><Show when={clipOffBeat(c, bpb()) !== 0}><b class="tl2-clip-off">Out of time</b></Show><small>{props.timeline.testTrack?.name ?? 'Song'}</small></div>
                        <svg class="tl2-wave" style={{ left: '0px', width: `${w()}px` }} viewBox={`0 0 ${Math.max(1, w())} ${WAVE_H}`} preserveAspectRatio="none">
                          <path d={tr.peaks() ? waveformPath(tr.peaks()!, secs().t0, secs().t1, w(), WAVE_H) : ''} />
                        </svg>
                        <i class="tl2-clip-edge" data-edge="start" />
                        <i class="tl2-clip-edge" data-edge="end" />
                      </div>
                    );
                  }}
                </For>
              </Show>
            </Show>
          </Show>
          <Show when={band()}>{(b) => <i class="tl2-clip-band" style={{ left: `${b().x0}px`, width: `${b().x1 - b().x0}px` }} />}</Show>
          <Show when={playX() != null}><i class="tl2-trk-ph" style={{ left: `${playX()}px` }} /></Show>
        </Show>
        <Show when={dropOver()}><span class="tl2-trk-dropmsg">Drop to import this song</span></Show>
        <Show when={props.timeline.testTrack && rowNotice()}><span class="tl2-trk-dropmsg tl2-trk-busy">{rowNotice()}</span></Show>
      </div>
      <Show when={pickAt()}>
        {(at) => (
          <div class="tl2-menu tl2-songpick" role="menu" aria-label="Choose a song" data-testid="tl2-songpick"
            style={{ position: 'fixed', left: `${at().left}px`, top: at().top != null ? `${at().top}px` : 'auto', bottom: at().bottom != null ? `${at().bottom}px` : 'auto', 'max-height': `${at().maxH}px`, 'overflow-y': 'auto' }}>
            <Show when={pickGroups().length} fallback={<span class="tl2-songpick-none">Nothing in the audio library yet.</span>}>
              <For each={pickGroups()}>
                {(g) => (
                  <>
                    <span class="tl2-songpick-h">{KIND_PLURAL[g.k]}</span>
                    <For each={g.list}>
                      {(t) => (
                        <button type="button" role="menuitem" data-id={t.id} disabled={!!t.arriving} onClick={() => choose(t)}>
                          <b>{t.name}</b><small>{t.arriving ? `arriving, ${Math.round(t.arriving.pct)}%` : t.analysing ? 'analysing' : `${Math.floor(t.durationSec / 60)}:${String(Math.round(t.durationSec % 60)).padStart(2, '0')}`}</small>
                        </button>
                      )}
                    </For>
                  </>
                )}
              </For>
            </Show>
            <i class="tl2-songpick-sep" />
            <button type="button" role="menuitem" data-testid="tl2-songpick-import" onClick={() => pickFile?.click()}><b>Import an audio file...</b><small>it joins the audio library</small></button>
            <Show when={props.onRemoveRow}>
              <button type="button" role="menuitem" data-testid="tl2-songpick-remove" onClick={() => { setPickAt(null); props.onRemoveRow!(); }}><b>Remove the song row</b></button>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
}

/** The detail panel when the track row is selected. */
export function TestTrackProps(props: {
  timeline: Timeline;
  selectedSectionName: string | null;
  Prop: (p: { label: string; children: JSX.Element }) => JSX.Element;
}): JSX.Element {
  const P = props.Prop;
  const tr = useTrack(() => props.timeline);
  /** The song has parts but the sections are still the timeline's own (2026-10-21): offer to switch. */
  const canStartFollowing = () => !props.timeline.testTrack?.partsFollow && !!planFor(props.timeline);
  void refreshTracks();
  // Where the song plays is the TIMELINE's (2026-10-13): OPBridge plays the song with the timeline from any start
  // (editor, pad, Pacer), so the choice travels with it. "" = where the timeline plays / that PC's default output.
  const pc = () => props.timeline.testTrack?.pc ?? '';
  const output = () => props.timeline.testTrack?.output ?? '';
  /** The PC that will actually make the sound: the chosen one, else this app's main OPBridge. */
  const soundPc = () => pc() || primaryBridge()?.nodeId || '';
  const [outputs, setOutputs] = createSignal<AudioOutput[]>([]);
  createEffect(on(soundPc, async (p) => { setOutputs(p ? await outputsOf(p) : []); }));
  const choose = (id: string) => {
    if (!id) { setTimelineTestTrack(props.timeline.id, null); return; }
    const t = tracks().find((x) => x.id === id);
    if (t) void linkSong(props.timeline.id, { id: t.id, name: t.name });
  };
  const playSection = () => {
    const m = tr.map(); const id = tr.id();
    if (!m || !id || !soundPc()) return;
    const name = props.selectedSectionName ?? (m.sections ?? [])[0]?.name;
    const mk = name ? markFor(m, name) : null;
    void play(mk ? { id, pc: soundPc(), output: output(), fromBar: mk.fromBar, loop: { fromBar: mk.fromBar, toBar: mk.toBar } } : { id, pc: soundPc(), output: output(), fromBar: 1 });
  };
  const setTempo = (c: TestTrackTempoChoice) => { const id = tr.id(); if (id) void patchBeatmap(id, { tempoChoice: c }); };
  const playing = () => { const s = status(); return !!s?.playing && s.id === tr.id(); };
  return (
    <>
      <Show when={unavailableReason()}>
        <P label="Songs"><div class="tl2-text tl2-trk-why">{unavailableReason()}</div></P>
      </Show>
      <P label="Import">
        <label class="tl2-btn tl2-trk-import" data-info="Import an audio file (MP3, WAV, FLAC, M4A...). OPBridge finds its beats, bar 1 and drums, then it is linked to this timeline. You can also drop a file on the Song row.">
          Import a song...
          <input type="file" accept="audio/*,.mp3,.wav,.flac,.m4a,.aac,.aif,.aiff,.ogg,.opus" style={{ display: 'none' }} onChange={(e) => { const f = e.currentTarget.files?.[0]; e.currentTarget.value = ''; if (f) void importAndLink(props.timeline, f); }} />
        </label>
      </P>
      <Show when={importLabel()}><P label="Importing"><div class="tl2-text" classList={{ 'tl2-trk-why': importing()?.state === 'failed' }}>{importLabel()}</div></P></Show>
      <P label="Song">
        {/* Each option says whether it is the one: a select's own value is set before the list arrives and falls
            back to "None" (seen in the smoke's screenshot, 2026-10-13). */}
        <select class="tl2-field" aria-label="Song" onChange={(e) => choose(e.currentTarget.value)}>
          <option value="" selected={!props.timeline.testTrack}>None</option>
          <Show when={props.timeline.testTrack && !tracks().some((t) => t.id === props.timeline.testTrack!.id)}>
            <option value={props.timeline.testTrack!.id} selected>{props.timeline.testTrack!.name}</option>
          </Show>
          <For each={tracks()}>
            {(t) => <option value={t.id} disabled={!!t.arriving} selected={t.id === props.timeline.testTrack?.id}>{t.name}{t.arriving ? ` (arriving, ${Math.round(t.arriving.pct)}%)` : !t.analysed ? ' (not analysed yet)' : ''}</option>}
          </For>
        </select>
      </P>
      <Show when={props.timeline.testTrack}>
        <P label="Mute">
          <div class="tl2-togs">
            <button type="button" class="tl2-btn" aria-pressed={!!props.timeline.testTrack?.mute} data-info="Mute: the timeline plays without its song." onClick={() => toggleSongFlag(props.timeline.id, 'mute')}>Mute</button>
            <button type="button" class="tl2-btn" aria-pressed={!!props.timeline.testTrack?.solo} data-info="Solo: only the song plays, and any lane soloed with it." onClick={() => toggleSongFlag(props.timeline.id, 'solo')}>Solo</button>
          </div>
        </P>
      </Show>
      <Show when={tr.map()}>
        <P label="Tempo">
          <div class="tl2-seg" data-info="Match the tempo you play the song at: a song can read as half or double what you play.">
            <button type="button" aria-pressed={(tr.map()!.tempoChoice ?? 'as-read') === 'half'} onClick={() => setTempo('half')}>{(tr.map()!.bpm / 2).toFixed(1)}</button>
            <button type="button" aria-pressed={(tr.map()!.tempoChoice ?? 'as-read') === 'as-read'} onClick={() => setTempo('as-read')}>{tr.map()!.bpm.toFixed(1)}</button>
            <button type="button" aria-pressed={(tr.map()!.tempoChoice ?? 'as-read') === 'double'} onClick={() => setTempo('double')}>{(tr.map()!.bpm * 2).toFixed(1)}</button>
          </div>
        </P>
        <P label="Parts">
          <div class="tl2-text">{(tr.map()!.sections ?? []).length ? (tr.map()!.sections ?? []).map((m) => `${m.name} ${m.fromBar}-${m.toBar}`).join(', ') : 'None yet: drag across the song on the Song parts row'}</div>
        </P>
        {/* 2026-10-21: a timeline with lights on its own sections switches to the song's parts only when asked. */}
        <Show when={canStartFollowing()}>
          <div class="tl2-actions">
            <button type="button" class="tl2-btn" data-testid="tl2-sections-from-song"
              data-info="One section per part of the song, in the song's order and at its lengths. Sections named like a part keep their lights."
              onClick={() => startFollowing(props.timeline.id)}>Use the song's parts as the sections</button>
          </div>
        </Show>
      </Show>
      <Show when={props.timeline.testTrack}>
        <P label="Play on">
          <select class="tl2-field" aria-label="Play on" data-info="The computer that plays the song. The song plays when the timeline plays, from the editor, a pad or a foot switch. Picking a computer also starts the timeline there, so the song stays in time; lights wired to another computer still light there." onChange={(e) => setTimelineTestTrackPlace(props.timeline.id, { pc: e.currentTarget.value })}>
            <option value="" selected={!pc()}>Where the timeline plays</option>
            <For each={playablePcs()}>{(p) => <option value={p.nodeId} selected={p.nodeId === pc()}>{p.name}</option>}</For>
          </select>
        </P>
        <P label="Output">
          <select class="tl2-field" aria-label="Output" onChange={(e) => setTimelineTestTrackPlace(props.timeline.id, { output: e.currentTarget.value })}>
            {/* OPBridge names outputs by device name, and "" is that computer's own default (S317 note 2). */}
            <option value="" selected={!output()}>The computer's default</option>
            <For each={outputs()}>{(o) => <option value={o.id} selected={o.id === output()}>{o.name}{o.default ? ' (default)' : ''}</option>}</For>
          </select>
        </P>
        <Show when={tr.map()}>
          <P label="Intro">
            <div class="tl2-seg" data-testid="tl2-intro" data-info="The music before bar 1 (a count-in, a spoken intro). Play it before the timeline's bar 1, or start the song on bar 1.">
              <button type="button" aria-pressed={!props.timeline.testTrack?.skipIntro} onClick={() => setTimelineSongSkipIntro(props.timeline.id, false)}>Play it ({tr.map()!.bar1_sec.toFixed(1)} s)</button>
              <button type="button" aria-pressed={!!props.timeline.testTrack?.skipIntro} onClick={() => setTimelineSongSkipIntro(props.timeline.id, true)}>Skip it</button>
            </div>
          </P>
          <Show when={props.timeline.testTrack?.skipIntro && primaryBridge() && !bridgeSkipsIntro(primaryBridge()!)}>
            <P label=""><div class="tl2-text tl2-trk-why" data-testid="tl2-intro-why">This OPBridge plays the intro until it is updated to skip it.</div></P>
          </Show>
          <Show when={props.timeline.testTrack?.clips && !props.timeline.testTrack?.skipIntro && !(props.timeline.testTrack.clips.some((c) => c.atBeat === 0 && c.fromBeat === 0))}>
            <P label=""><div class="tl2-text">With these clips no intro plays: the first clip does not start on the song's bar 1.</div></P>
          </Show>
          <P label="Clips">
            <div class="tl2-text" data-testid="tl2-clips-info">
              {props.timeline.testTrack?.clips
                ? `${props.timeline.testTrack.clips.length} clip${props.timeline.testTrack.clips.length === 1 ? '' : 's'}: drag to move, drag an edge to trim, right-click to split.`
                : 'Plays the section marks. Right-click the song row to split it into clips.'}
              <Show when={props.timeline.testTrack?.clips && primaryBridge() && !bridgeSongClips(primaryBridge()!)}>
                <span class="tl2-trk-why"> This OPBridge plays the song by its section marks until it is updated to play clips.</span>
              </Show>
            </div>
          </P>
          <Show when={props.timeline.testTrack?.clips}>
            <div class="tl2-actions">
              <button type="button" class="tl2-btn" onClick={() => setTimelineSongClips(props.timeline.id, null)}>Back to the section marks</button>
            </div>
          </Show>
        </Show>
        <div class="tl2-actions">
          <Show
            when={!playing()}
            fallback={<button type="button" class="tl2-btn" onClick={() => void stop()}>Stop the song</button>}
          >
            <button type="button" class="tl2-btn" disabled={!tr.map() || !soundPc()} onClick={playSection}>
              {props.selectedSectionName && tr.map() && markFor(tr.map()!, props.selectedSectionName) ? `Play ${props.selectedSectionName}, looping` : 'Play the song'}
            </button>
          </Show>
        </div>
        <Show when={tr.map()}>
          <P label="Grid"><div class="tl2-text">{gridBpm(tr.map()!).toFixed(1)} BPM, bar 1 at {tr.map()!.bar1_sec.toFixed(2)} s, {songBars(tr.map()!)} bars of {barSeconds(tr.map()!).toFixed(2)} s</div></P>
        </Show>
      </Show>
      <Show when={lastError()}><P label="OPBridge said"><div class="tl2-text tl2-trk-why">{lastError()}</div></P></Show>
    </>
  );
}
